import firebase from '../config/firebase';
import { resolveUserCountryCode } from '../data/countries';

const getFreshIdToken = async () => {
  const user = firebase.auth().currentUser;
  if (user) {
    try {
      const token = await user.getIdToken(true); // force refresh
      localStorage.setItem('token', token);
      return token;
    } catch (error) {
      console.error('[AUTH] Failed to get fresh token:', error);
      return localStorage.getItem('token');
    }
  }
  return localStorage.getItem('token');
};

const setupAuthListener = (onAuthChange) => {
  firebase.auth().onIdTokenChanged(async (user) => {
    if (user) {
      const token = await user.getIdToken();
      localStorage.setItem('token', token);
      console.log('[AUTH] Token auto-refreshed');
      if (onAuthChange) onAuthChange(user);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      console.log('[AUTH] User signed out');
      if (onAuthChange) onAuthChange(null);
    }
  });
};

const firebaseEmailLogin = async (email, password) => {
  const result = await firebase.auth().signInWithEmailAndPassword(email, password);
  const idToken = await result.user.getIdToken(true);
  return { user: result.user, idToken };
};

const getVerificationActionCodeSettings = () => ({
  url: `${window.location.origin}/login`,
  handleCodeInApp: false,
});

const userNeedsEmailVerification = (user) => Boolean(user && !user.emailVerified);

const reloadCurrentUser = async () => {
  const user = firebase.auth().currentUser;
  if (!user) return null;
  await user.reload();
  const refreshed = firebase.auth().currentUser;
  if (refreshed) {
    const token = await refreshed.getIdToken(true);
    localStorage.setItem('token', token);
  }
  return refreshed;
};

const sendVerificationEmail = async (user = firebase.auth().currentUser) => {
  if (!user) throw new Error('No signed-in user to verify.');
  await user.sendEmailVerification(getVerificationActionCodeSettings());
};

const resendVerificationEmail = async () => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Please sign in again to resend the verification email.');
  await sendVerificationEmail(user);
};

const firebaseEmailRegister = async (email, password, displayName) => {
  const result = await firebase.auth().createUserWithEmailAndPassword(email, password);
  await result.user.updateProfile({ displayName });
  await sendVerificationEmail(result.user);
  const idToken = await result.user.getIdToken(true);
  return { user: result.user, idToken, needsEmailVerification: true };
};

const googleSignIn = async (onSuccess, countryCode = "US") => {
  const { default: apiClient } = await import('./api');

  const provider = new firebase.auth.GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');

  window.__coownSigningIn = true;
  try {
    console.log('[GOOGLE AUTH] Starting popup...');
    const result = await firebase.auth().signInWithPopup(provider);
    console.log('[GOOGLE AUTH] Popup success, user:', result.user?.email);

    const idToken = await result.user.getIdToken(true);
    console.log('[GOOGLE AUTH] Got ID token, length:', idToken?.length);

    const nameParts = (result.user.displayName || "User").split(" ");
    const profile = {
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(" ") || "",
      country: resolveUserCountryCode(countryCode),
    };

    console.log('[GOOGLE AUTH] Calling backend /auth/google with ID token...');
    const res = await apiClient.googleSignIn(idToken, profile);
    console.log('[GOOGLE AUTH] Backend response:', res);

    const isNewUser = Boolean(res.data?.isNewUser);
    let needsEmailVerification = userNeedsEmailVerification(result.user);
    if (isNewUser) {
      await result.user.reload();
      needsEmailVerification = userNeedsEmailVerification(firebase.auth().currentUser);
      if (needsEmailVerification) {
        await sendVerificationEmail(firebase.auth().currentUser);
      }
    }

    if (res.data?.user?.isActive === false) {
      await firebase.auth().signOut();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      throw new Error('Your account has been suspended. Contact support@joincoown.com if you believe this is an error.');
    }

    const freshToken = await firebase.auth().currentUser.getIdToken(true);
    localStorage.setItem('token', freshToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    console.log('[GOOGLE AUTH] Auth complete! User stored.');
    onSuccess({
      ...res.data.user,
      isNewUser,
      needsEmailVerification,
    });
  } catch (error) {
    console.error('[GOOGLE AUTH] ========== FULL ERROR ==========');
    console.error('[GOOGLE AUTH] Error object:', error);
    console.error('[GOOGLE AUTH] Error code:', error.code);
    console.error('[GOOGLE AUTH] Error message:', error.message);
    console.error('[GOOGLE AUTH] ===================================');

    let friendlyMessage = error.message || 'Unknown error';
    if (error.code === 'auth/popup-closed-by-user') {
      friendlyMessage = 'Sign-in popup was closed. Please try again.';
    } else if (error.code === 'auth/popup-blocked') {
      friendlyMessage = 'Popup was blocked. Please allow popups for this site.';
    } else if (error.code === 'auth/cancelled-popup-request') {
      friendlyMessage = 'Sign-in was cancelled. Please try again.';
    } else if (error.code === 'auth/network-request-failed') {
      friendlyMessage = 'Network error. Please check your connection.';
    } else if (error.code === 'auth/unauthorized-domain') {
      friendlyMessage = 'This domain is not authorized for Google Sign-In. Please contact support.';
    } else if (error.code === 'auth/user-disabled') {
      friendlyMessage = 'Your account has been suspended. Contact support@joincoown.com if you believe this is an error.';
    }

    if (String(friendlyMessage).includes('suspended')) {
      try {
        await firebase.auth().signOut();
      } catch (_) {}
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    alert('Google sign-in failed: ' + friendlyMessage);
    throw error;
  } finally {
    window.__coownSigningIn = false;
  }
};

export {
  getFreshIdToken,
  setupAuthListener,
  firebaseEmailLogin,
  firebaseEmailRegister,
  googleSignIn,
  sendVerificationEmail,
  resendVerificationEmail,
  reloadCurrentUser,
  userNeedsEmailVerification,
};
